"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { GradientButton } from "../layout/gradient-button";
import { GoogleOAuthButton } from "@/components/auth/google-oauth-button";
import { env } from "@/app/env";

// Zod schema for login (email only)
const loginSchema = z.object({
  email: z.email({ message: "Invalid email address" }),
});

type LoginSchema = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSubmit: (email: string) => Promise<any>;
}

export default function LoginForm({ onSubmit }: LoginFormProps) {
  const [loading, setLoading] = useState(false);

  const form = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "" },
  });

  const handleSubmit = async (values: LoginSchema) => {
    setLoading(true);
    const { error } = await onSubmit(values.email);
    if (error) {
      if (error.toLowerCase().includes("signups not allowed")) {
        form.setError("email", {
          type: "manual",
          message: "This email is not registered. Please register first.",
        });
      } else {
        form.setError("email", {
          type: "manual",
          message: error,
        });
      }
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  return (
    <div className="relative min-h-[370px]">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4 py-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email address</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder="Your email"
                    className="rounded-2xl h-12"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <GradientButton type="submit" className="w-full mt-4" disabled={loading}>
            {loading ? "Sending OTP..." : "Send OTP"}
          </GradientButton>
          {env.NEXT_PUBLIC_SENTRY_ENVIRONMENT === "development" && (
            <>
              <div className="flex items-center my-4">
                <div className="flex-1 h-px bg-muted-foreground/20" />
                <span className="text-muted-foreground text-xs px-2">or</span>
                <div className="flex-1 h-px bg-muted-foreground/20" />
              </div>
              <GoogleOAuthButton />
            </>
          )}
          <div className="text-sm text-center text-muted-foreground mt-4">
            By logging in, you agree to our{" "}
            <a href="/legal/terms-of-service" className="underline text-blue-600">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="/legal/privacy-policy" className="underline text-blue-600">
              Privacy Policy
            </a>
          </div>
        </form>
      </Form>
    </div>
  );
}
